import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from 'hooks/useAuth';
import { useIsMobile } from '@/components/ui/hooks/use-mobile';
import { ConsoleHeader } from './ConsoleHeader';
import { ConsoleSidebar } from './ConsoleSidebar';
import { useSidebarMode } from './use-sidebar-mode';
import { SIDE_PANEL_SLOT_ID, WorkspacePanel } from './WorkspacePanel';
import { LogPanel } from './LogPanel';
import { GlobalAlerts } from './GlobalAlerts';
import { GlobalAnnouncement } from '../../GlobalAnnouncement';
import { ActivationBanner } from '../../ActivationBanner';
import { ReadOnlyNotice } from './ReadOnlyNotice';
import { getMenuLinks } from '@/menu-links';
import { useGlobal } from 'hooks/useGlobal';
import { TourLayer } from '@/tours';

type DashboardLayoutProps = { children: React.ReactNode };
export const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const MENU_LINKS = getMenuLinks({ user });

  const [pageTitle, setPageTitle] = useGlobal<string>(
    'PAGE_TITLE',
    () => getPageMeta(location.pathname, MENU_LINKS).title,
  );
  const [, setPageBeta] = useGlobal<boolean>('PAGE_BETA', () => getPageMeta(location.pathname, MENU_LINKS).beta);

  useEffect(() => {
    if (location.pathname) {
      const meta = getPageMeta(location.pathname, MENU_LINKS);
      setPageTitle(meta.title);
      setPageBeta(meta.beta);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isLoading && !user) {
      if (!location.pathname.startsWith('/auth')) {
        window.location.href = `/auth/signin?from=${location.pathname}`;
      }
    }
  }, [isLoading, user]);

  // Two separate states on purpose. On a desktop the sidebar is furniture, always
  // there, full or folded to its icon rail, and that choice is remembered; on a phone
  // it is a drawer over the content, and a drawer that reopens itself on every visit
  // is a nuisance. A docked AI chat folds the desktop nav to the rail while it is
  // open (useSidebarMode says how the reader can overrule that).
  const isMobile = useIsMobile();
  const desktop = useSidebarMode();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const isRail = !isMobile && desktop.mode === 'rail';
  // Full on a desktop, open on a phone: what the header toggle reports as expanded.
  const isSidebarExpanded = isMobile ? isDrawerOpen : !isRail;

  const toggleSidebar = () => (isMobile ? setDrawerOpen((open) => !open) : desktop.toggle());

  // Navigating is the drawer's job done. Leaving it open would cover the page it
  // just took you to.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Cmd/Ctrl+B, as the shell this replaced had. Ignored while typing, so it cannot
  // swallow a B in a search field or an experiment name.
  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== 'b' || !(ev.metaKey || ev.ctrlKey)) return;
      const target = ev.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) return;
      ev.preventDefault();
      toggleSidebar();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // The desktop toggle flips a different state while a chat is open, so the
    // listener has to be rebuilt when one opens or closes.
  }, [isMobile, desktop.isSidePanelOpen]);

  // On a phone the drawer covers the page under an opaque scrim, which makes it a
  // modal dialog and not a panel: without this, focus stayed on the header's toggle
  // and Tab walked invisibly through the page behind the scrim, and there was no way
  // back out from the keyboard at all.
  //
  // Same window-level keydown shape as Cmd/Ctrl+B above rather than a second
  // mechanism; the two cannot contend because each returns early on a key it does not
  // own. A popover opened from inside the drawer portals outside it and handles its
  // own Escape and Tab, so this only takes either key while focus is in the drawer —
  // which layers correctly: the first Escape closes the popover, the second the drawer.
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const isDrawerModal = isMobile && isDrawerOpen;

  useEffect(() => {
    const drawer = drawerRef.current;
    if (!isDrawerModal || !drawer) return;

    // Whatever opened it — the header toggle by tap, or nothing at all when the
    // shortcut did it. Focus goes back there on close so the reader is not dropped
    // at the top of the document.
    const opener = document.activeElement as HTMLElement | null;
    drawer.focus();

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape' && ev.key !== 'Tab') return;
      const target = ev.target as Node | null;
      if (!target || !drawer.contains(target)) return;

      if (ev.key === 'Escape') {
        ev.preventDefault();
        setDrawerOpen(false);
        return;
      }

      // Collapsed accordion sections are in the DOM but hidden, so an offsetParent
      // check is what separates what can really be reached from what cannot.
      const focusable = Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.offsetParent !== null,
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      const isLeavingBackwards = ev.shiftKey && (ev.target === first || ev.target === drawer);
      const isLeavingForwards = !ev.shiftKey && ev.target === last;
      if (!isLeavingBackwards && !isLeavingForwards) return;

      ev.preventDefault();
      (isLeavingBackwards ? last : first).focus();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      opener?.focus();
    };
  }, [isDrawerModal]);

  if (location.pathname.startsWith('/auth')) return children;

  if (!user) return null;

  // Fixed-height desk: the header sits on it, the body fills what is left, and
  // the workspace panel inside the body owns the only vertical scroll. Every
  // flex child in that chain needs min-h-0/min-w-0 or the scroll area grows the
  // grid instead of scrolling.
  //
  // `fixed inset-0`, not `h-screen`: the desk is taken out of the document flow
  // entirely, so it cannot contribute height to the body and the window has
  // nothing to scroll. h-screen plus overflow-hidden was not enough - the desk was
  // still a block in the flow, and anything that overflowed it (a tall alert, a
  // dialog, 100vh disagreeing with the visual viewport on mobile) put the whole
  // shell on a scrollbar and let you drag the header and sidebar out of view.
  return (
    <div className="bg-background fixed inset-0 flex flex-col gap-2.5 overflow-hidden p-2 md:p-3">
      <ConsoleHeader onToggleSidebar={toggleSidebar} isSidebarExpanded={isSidebarExpanded} />

      <div className="relative flex min-h-0 flex-1 gap-2.5">
        {/* On a phone the sidebar is a drawer over the content with a scrim, not a
            column beside it: 228px of a 375px viewport would leave the page about
            a hundred pixels wide. */}
        {isMobile && isDrawerOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 z-40 cursor-default bg-black/50"
          />
        )}

        {/* The dialog semantics go on a wrapper: `ConsoleSidebar` is shared with the
            desktop shell, where the same nav is furniture rather than a modal. */}
        {isMobile ? (
          isDrawerOpen && (
            <div
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label="Navigation"
              tabIndex={-1}
              className="absolute inset-y-0 left-0 z-50 flex"
            >
              <ConsoleSidebar />
            </div>
          )
        ) : (
          <ConsoleSidebar isRail={isRail} />
        )}

        <main className="flex min-h-0 min-w-0 flex-1 flex-col gap-2.5">
          <GlobalAnnouncement />
          <GlobalAlerts />
          <ActivationBanner />
          <ReadOnlyNotice />
          <WorkspacePanel>{children}</WorkspacePanel>
          {/* Vertical space is the scarce resource on a phone, and the log is a
              secondary feed - the header's alert pill still carries the signal. */}
          {!isMobile && <LogPanel />}
        </main>

        {/* WorkspaceSidePanel renders here (the results AI chat, Ask on Learnings): a column beside
            the workspace from xl, over its right edge below that. Empty, and so hidden, until
            something is rendered. The nav folds to its rail while it is in use, at every width:
            below xl the panel covers the workspace's edge rather than narrowing it, but the
            172px the labels give back still widens what is left uncovered. */}
        <div
          id={SIDE_PANEL_SLOT_ID}
          className="absolute inset-y-0 right-0 z-30 flex min-h-0 w-full max-w-[420px] flex-col empty:hidden xl:static xl:w-[400px] xl:max-w-none xl:shrink-0"
        />
      </div>

      <TourLayer />
    </div>
  );
};

/** Everything a Tab can land on inside the drawer, in document order. */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Resolves the title and the beta flag together, from the same matched link.
 * Two separate lookups could disagree if one matched an exact url and the other
 * a pattern, which would show a Beta badge beside the wrong title.
 */
const getPageMeta = (pathname: string, MENU_LINKS: any): { title: string; beta: boolean } => {
  const matched = findMenuLink(pathname, MENU_LINKS);
  if (!matched) return { title: 'Four Oh Four', beta: false };
  return { title: matched.title, beta: !!matched.beta };
};

const findMenuLink = (pathname: string, MENU_LINKS: any) => {
  // Exact match first
  for (const item of MENU_LINKS) {
    if (item.url === pathname) return item;
    if (item.children) {
      const child = item.children.find((c: any) => c.url === pathname);
      if (child) return child;
    }
  }

  // Pattern matching for dynamic routes (e.g., /experiments/:id -> /experiments/123)
  for (const item of MENU_LINKS) {
    if (item.url && matchesPattern(pathname, item.url)) {
      return item;
    }
    if (item.children) {
      const child = item.children.find((c: any) => c.url && matchesPattern(pathname, c.url));
      if (child) return child;
    }
  }

  return null;
};

const matchesPattern = (pathname: string, pattern: string): boolean => {
  const patternSegments = pattern.split('/').filter(Boolean);
  const pathSegments = pathname.split('/').filter(Boolean);

  if (patternSegments.length !== pathSegments.length) return false;

  for (let i = 0; i < patternSegments.length; i++) {
    const segment = patternSegments[i];
    const pathSegment = pathSegments[i];
    // Check if segment is a dynamic parameter (starts with :)
    if (segment && !segment.startsWith(':') && segment !== pathSegment) {
      return false;
    }
  }

  return true;
};
